import { useState } from "react";
import { CategoryChips } from "./CategoryChips";
import { ReasonDropdown } from "./ReasonDropdown";
import type { FlagCategory } from "./flagConstants.";
import { X, Flag, ChevronUp, ChevronDown, ArrowRight } from "lucide-react";
import "./icons.css";

function formatTime(d: Date) {
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
}
function adjustSec(d: Date, delta: number) {
  return new Date(d.getTime() + delta * 1000);
}

/* ─── QuickFlagModal ──────────────────────────────────────────────────────── */

interface QuickFlagModalProps {
  start: Date;
  end: Date;
  onSubmit: (cat: FlagCategory, reason: string, start: Date, end: Date) => void;
  onCancel: () => void;
}

export function QuickFlagModal({
  start,
  end,
  onSubmit,
  onCancel,
}: QuickFlagModalProps) {
  const [cat, setCat] = useState<FlagCategory | null>(null);
  const [reason, setReason] = useState("");
  const canSubmit = !!cat && !!reason;

  return (
    <FlagModalShell
      title="Quick Flag ±5s"
      iconClass="ti ti-flag"
      onClose={onCancel}
    >
      {/* Time row */}
      <div className="time-row">
        <div className="time-block">
          <div className="time-label">Start</div>
          <div className="time-val">{formatTime(start)}</div>
        </div>
        <div className="time-arrow">
          <ArrowRight size={14} style={{ color: "rgba(255,255,255,0.2)" }} />
        </div>
        <div className="time-block">
          <div className="time-label">End</div>
          <div className="time-val">{formatTime(end)}</div>
        </div>
      </div>

      <div className="section-label">Category</div>
      <CategoryChips
        selected={cat}
        onSelect={(c) => {
          setCat(c);
          setReason("");
        }}
      />
      <ReasonDropdown category={cat} value={reason} onChange={setReason} />
      <ModalFooter
        onCancel={onCancel}
        onSubmit={() => canSubmit && onSubmit(cat!, reason, start, end)}
        disabled={!canSubmit}
      />
    </FlagModalShell>
  );
}

/* ─── StopFlagModal ───────────────────────────────────────────────────────── */

interface StopFlagModalProps {
  rawStart: Date;
  rawEnd: Date;
  onSubmit: (cat: FlagCategory, reason: string, start: Date, end: Date) => void;
  onCancel: () => void;
}

export function StopFlagModal({
  rawStart,
  rawEnd,
  onSubmit,
  onCancel,
}: StopFlagModalProps) {
  const [start, setStart] = useState(rawStart);
  const [end, setEnd] = useState(rawEnd);
  const [cat, setCat] = useState<FlagCategory | null>(null);
  const [reason, setReason] = useState("");

  const durSec = Math.max(
    0,
    Math.round((end.getTime() - start.getTime()) / 1000),
  );
  const canSubmit = !!cat && !!reason && durSec > 0;

  return (
    <FlagModalShell
      title=" Flag Duration"
      iconClass="ti ti-flag-2"
      onClose={onCancel}
    >
      {/* Time row with adj controls */}
      <div className="time-row">
        <div className="time-block">
          <div className="time-label">Start</div>
          <div className="time-val">{formatTime(start)}</div>
        </div>
        <div className="time-arrow">→</div>
        <div className="time-block">
          <div className="time-label">End</div>
          <div className="time-val">{formatTime(end)}</div>
        </div>
        <div className="edit-controls">
          <AdjGroup
            label="Start"
            onAdjust={(d) => setStart(adjustSec(start, d))}
          />
          <AdjGroup label="End" onAdjust={(d) => setEnd(adjustSec(end, d))} />
        </div>
      </div>

      {/* Duration row */}
      <div className="duration-row" style={{ display: "block" }}>
        <div className="duration-label">Duration flagged</div>
        <div className="duration-val">
          {Math.floor(durSec / 60)}:{String(durSec % 60).padStart(2, "0")}
        </div>
      </div>

      <div className="section-label">Category</div>
      <CategoryChips
        selected={cat}
        onSelect={(c) => {
          setCat(c);
          setReason("");
        }}
      />
      <ReasonDropdown category={cat} value={reason} onChange={setReason} />
      <ModalFooter
        onCancel={onCancel}
        onSubmit={() => canSubmit && onSubmit(cat!, reason, start, end)}
        disabled={!canSubmit}
      />
    </FlagModalShell>
  );
}

/* ─── Shared sub-components ─────────────────────────────────────────────── */

function FlagModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  iconClass: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    /* Exact prototype overlay — position:fixed, paddingBottom:74px */
    <div
      className="popup-overlay show"
      style={{ position: "fixed" }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="popup">
        <div className="popup-header">
          <div className="popup-title">
            <Flag size={15} style={{ color: "#FCD34D" }} />

            {title}
          </div>
          <button className="popup-close" type="button" onClick={onClose}>
            <X size={13} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function AdjGroup({
  label,
  onAdjust,
}: {
  label: string;
  onAdjust: (d: number) => void;
}) {
  return (
    <div className="adj-group">
      <div className="adj-label">{label}</div>
      <div className="adj-btns">
        <button type="button" className="adj-btn" onClick={() => onAdjust(1)}>
          <ChevronUp size={10} />
        </button>
        <button type="button" className="adj-btn" onClick={() => onAdjust(-1)}>
          <ChevronDown size={10} />
        </button>
      </div>
    </div>
  );
}

function ModalFooter({
  onCancel,
  onSubmit,
  disabled,
}: {
  onCancel: () => void;
  onSubmit: () => void;
  disabled: boolean;
}) {
  return (
    <div className="popup-actions">
      <button type="button" className="btn-cancel" onClick={onCancel}>
        Cancel
      </button>
      <button
        type="button"
        className="btn-submit"
        onClick={onSubmit}
        disabled={disabled}
      >
        <Flag size={13} />
        Submit Flag
      </button>
    </div>
  );
}
